import React from "react";

import { IconCloseCircleFilled } from "~/components/icon";
import { cx } from "~/tools/class-variance";
import { prop } from "~/tools/prop";
import { RemoteData } from "~/tools/remote-data";

import { queryOverview } from "../../../../queries/overview";
import { useQuery } from "../../../../queries/react-query";

import type { RelatedResourcesDrawerState } from "./state";

interface UseQueryCountParams extends Pick<RelatedResourcesDrawerState, "timestamp"> {
	stql: undefined | string;
	presentationIdentifier: undefined | string;
}

function useQueryCount({ timestamp, stql, presentationIdentifier }: UseQueryCountParams) {
	const { error, data = RemoteData.loading() } = useQuery({
		enabled: stql != null && presentationIdentifier != null,
		keepPreviousData: true,
		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,

		select: (response) => {
			if (response._type === "OverviewPageResult") {
				return RemoteData.success(response.pagination.total);
			}

			return RemoteData.failure(response);
		},

		...queryOverview({
			urn: presentationIdentifier ?? "",
			timestamp,
			stql: stql ?? "",
			/**
			 * All ResourceTabs are rendered at the same time
			 * while their corresponding content might be not rendered at all.
			 * So it should fetch the minimal page size to get the total count only.
			 */
			limit: 1,
		}),
	});

	return RemoteData.all({
		error: error == null ? RemoteData.success(null) : RemoteData.failure(error),
		data,
	}).transform(prop("data"));
}

interface ResourceTabProps extends UseQueryCountParams {
	className?: string;
	title: undefined | string;
}

export const ResourceTab = React.forwardRef<React.ComponentRef<"button">, ResourceTabProps>(
	({ className, timestamp, stql, presentationIdentifier, title, ...props }, ref) => {
		const result = useQueryCount({ timestamp, stql, presentationIdentifier });

		return (
			<button
				type="button"
				{...props}
				className={cx("flex gap-2", className)}
				data-count={result.getOrElse(undefined)}
				ref={ref}
			>
				<span className="state-loading:skeleton state-loading:w-20" data-state={title == null ? "loading" : undefined}>
					{title}
				</span>

				{result.match<React.ReactNode>({
					loading: () => <span className="skeleton w-4" />,

					failure: () => <IconCloseCircleFilled className="text-red-500" />,

					success: prop("value"),
				})}
			</button>
		);
	},
);
